import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { useStaticQuery, graphql } from 'gatsby'
import { Section, SectionTitle, Container } from 'chaoskit/src/components'
import { text } from 'chaoskit/src/assets/styles/utility'

import GradientList from '../global/GradientList'

import Data from '~components/Data'
import { ReactComponent as IdentifySVG } from '~media/home/identify.svg'
import { ReactComponent as AccelerateSVG } from '~media/home/accelerate.svg'
import { ReactComponent as ValidateSVG } from '~media/home/validate.svg'
import { ReactComponent as CustomerTrustSVG } from '~media/home/customer-trust.svg'
import { ReactComponent as ShortenDevelopmentSVG } from '~media/home/shorten-development.svg'
import { sectionSeparator } from '~styles/utility'

const StylesHomeVariables = {
  preventImageSize: 80,
}

const HomeSection = ({ separator, ...rest }) => {
  const theme = useTheme()

  return (
    <Section
      space="xlarge"
      css={[
        separator &&
          sectionSeparator(theme, {
            start: theme.brand.teal,
            stop: theme.color.primary.base,
          }),
      ]}
      {...rest}
    />
  )
}

HomeSection.propTypes = {
  separator: PropTypes.bool,
}

const GridBlock = ({ size = 'base', ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          display: 'grid',
          gap: theme.space.base,
          alignItems: 'center',

          [theme.mq.medium]: {
            gap: theme.space[size],
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          },
        },
      ]}
      {...rest}
    />
  )
}

GridBlock.propTypes = {
  size: PropTypes.oneOf(['base', 'large']),
}

const GridContent = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        [theme.mq.medium]: {
          gridColumn: '7 / span 6',
          gridRow: 1,
        },
      }}
      {...props}
    />
  )
}

const PreventIcon = ({ icon }) => {
  const theme = useTheme()

  let Component

  // eslint-disable-next-line default-case
  switch (icon) {
    case 'identify':
      Component = IdentifySVG
      break
    case 'accelerate':
      Component = AccelerateSVG
      break
    case 'validate':
      Component = ValidateSVG
      break
  }

  return (
    <Component
      css={{
        margin: '0 auto',
        width: StylesHomeVariables.preventImageSize / 1.5,
        height: StylesHomeVariables.preventImageSize / 1.5,
        objectFit: 'contain',

        [theme.mq.medium]: {
          width: StylesHomeVariables.preventImageSize,
          height: StylesHomeVariables.preventImageSize,
        },
      }}
    />
  )
}

PreventIcon.propTypes = {
  icon: PropTypes.oneOf(['identify', 'accelerate', 'validate']),
}

const HomeOverview = ({ hideFirstSeparator }) => {
  const theme = useTheme()

  const {
    file: {
      childMdx: { frontmatter: pageData },
    },
  } = useStaticQuery(graphql`
    query HomeOverviewPageData {
      file(name: { eq: "home" }, absolutePath: { regex: "/src/data/" }) {
        childMdx {
          frontmatter {
            prevent {
              heading
              blurb
              items {
                icon
                title
                blurb
              }
            }
            shortenDevelopment {
              heading
              blurb
              items {
                blurb
              }
            }
            customerTrust {
              heading
              blurb
              items {
                blurb
              }
            }
          }
        }
      }
    }
  `)

  return (
    <Container css={{ position: 'relative', zIndex: 1 }}>
      <HomeSection separator={!hideFirstSeparator}>
        <SectionTitle
          as="h2"
          title={<Data>{pageData.prevent.heading}</Data>}
          sub={<Data>{pageData.prevent.blurb}</Data>}
        />
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridAutoColumns: '1fr',
              gridAutoFlow: 'column',
            },
          }}
        >
          {pageData.prevent.items.map((item) => (
            <div
              key={item.title}
              css={{
                display: 'grid',
                gap: theme.space.base,
                alignItems: 'center',
                gridTemplateColumns: `${
                  StylesHomeVariables.preventImageSize / 1.5
                }px 1fr`,

                [theme.mq.medium]: {
                  gridTemplateColumns: `${StylesHomeVariables.preventImageSize}px 1fr`,
                  alignItems: 'start',
                },
              }}
            >
              <div>
                <PreventIcon icon={item.icon} />
              </div>
              <div>
                <div
                  css={{
                    ...text.expanded(theme),
                    fontWeight: theme.fontWeight.bold,
                    ...theme.text.large,
                    marginBottom: theme.space.small,
                  }}
                >
                  {item.title}
                </div>
                <div css={{ ...theme.text.small }}>
                  <Data>{item.blurb}</Data>
                </div>
              </div>
            </div>
          ))}
        </div>
      </HomeSection>
      <HomeSection separator>
        <SectionTitle
          as="h2"
          title={<Data>{pageData.shortenDevelopment.heading}</Data>}
          sub={<Data>{pageData.shortenDevelopment.blurb}</Data>}
        />
        <GridBlock>
          <ShortenDevelopmentSVG
            css={{
              width: '100%',
              maxWidth: 400,

              [theme.mq.small__max]: {
                margin: '0 auto',
              },

              [theme.mq.medium]: {
                maxWidth: 600,
                gridColumn: '1 / span 7',
                gridRow: 1,
              },
            }}
          />
          <GridContent>
            <GradientList items={pageData.shortenDevelopment.items} />
          </GridContent>
        </GridBlock>
      </HomeSection>
      <HomeSection separator>
        <SectionTitle
          as="h2"
          title={<Data>{pageData.customerTrust.heading}</Data>}
          sub={<Data>{pageData.customerTrust.blurb}</Data>}
        />
        <GridBlock size="large">
          <CustomerTrustSVG
            css={{
              width: '100%',
              maxWidth: 400,

              [theme.mq.small__max]: {
                margin: '0 auto',
              },

              [theme.mq.medium]: {
                maxWidth: 500,

                gridColumn: '1 / span 6',
                gridRow: 1,
              },
            }}
          />

          <GridContent>
            <GradientList items={pageData.customerTrust.items} />
          </GridContent>
        </GridBlock>
      </HomeSection>
    </Container>
  )
}

HomeOverview.propTypes = {
  hideFirstSeparator: PropTypes.bool,
}

export default HomeOverview
